import React, { useCallback, useState } from 'react';
import { Tab, TabPanel, Tabs } from '@dynamon-as/ui';
import { useIsMobile } from '@shared/hooks';
import styles from './RequestChatLayout.module.css';

/**
 * Props for RequestChatLayout component
 */
export interface RequestChatLayoutProps {
    children: React.ReactNode;
    chat: React.ReactNode;
    chatBadge?: number;
}

/**
 * Layout component that shows content + chat on desktop,
 * and tab-switched content/chat on mobile.
 */
export const RequestChatLayout = React.memo(function RequestChatLayout({
    children,
    chat,
    chatBadge,
}: RequestChatLayoutProps): React.ReactElement {
    const isMobile = useIsMobile();
    const [activeTab, setActiveTab] = useState<'content' | 'chat'>('content');

    const handleTabChange = useCallback(
        (_event: React.SyntheticEvent, value: string): void => {
            setActiveTab(value as 'content' | 'chat');
        },
        [],
    );

    // Desktop layout
    if (!isMobile) {
        return (
            <div
                className={styles.layout}
                data-testid="request-chat-layout-desktop"
            >
                <div className={styles.content}>{children}</div>
                <aside className={styles.chatPanel}>{chat}</aside>
            </div>
        );
    }

    return (
        <div
            className={styles.mobile}
            data-testid="request-chat-layout-mobile"
        >
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
            >
                <Tab
                    value="content"
                    label="Innhold"
                />
                <Tab
                    value="chat"
                    label={
                        chatBadge && chatBadge > 0
                            ? `Chat (${chatBadge})`
                            : 'Chat'
                    }
                />
            </Tabs>
            <TabPanel
                index="content"
                activeIndex={activeTab}
            >
                <div className={styles.contentTab}>{children}</div>
            </TabPanel>
            <TabPanel
                index="chat"
                activeIndex={activeTab}
            >
                <div className={styles.chatTab}>{chat}</div>
            </TabPanel>
        </div>
    );
});
